import type { BreadcrumbViewModel } from 'autogen/swagger/all';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import containerFluid from 'components/Layout/shared/styles';
import { invisibleScrollbar } from 'modules/helpers/mixins';
import BreadcrumbItem from './components/BreadcrumbItem';
import BackButton from './components/BackButton';

export type BreadcrumbsProps = {
    model: BreadcrumbViewModel;
};

const Background = styled.div`
    background: ${({ theme }) => theme.ColorBackgroundPrimary};
`;

const Wrapper = styled.div`
    position: relative;
    display: flex;
    align-items: stretch;
    height: 50px;
    ${containerFluid}
`;

const BreadcrumbsContainer = styled.div`
    display: flex;
    flex-direction: row-reverse; // row-reverse is used to position items to the right side as "justify-content: flex-end;" will not create scroll
    color: ${({ theme }) => theme.ComponentsCtaInlineSecondaryText};
    overflow-y: hidden;
    overflow-x: scroll;
    white-space: nowrap;

    ${invisibleScrollbar()}

    ${breakpoint.up(sizes.md)} {
        flex-direction: row;
    }
`;

const Items = styled.div`
    display: inline-flex;
    align-items: center;
    padding: 0 ${({ theme }) => theme.Spacing16};

    ${breakpoint.up(sizes.md)} {
        padding: 0 ${({ theme }) => theme.Spacing16};
    }
`;

const Divider = styled.div`
    display: inline;
    padding: 0 ${({ theme }) => theme.Spacing8};

    &:first-of-type {
        display: none;
    }
`;

const Spacer = styled.div`
    height: ${({ theme }) => theme.Spacing16};
    background: ${({ theme }) => theme.ColorBackgroundPrimary};
`;

export default function Breadcrumbs(props: BreadcrumbsProps) {
    const { model } = props;
    const { items, fallbackNavigateBackItem, showBackButton, isChanelContext } = model;
    const [showBackButtonOn, setShowBackButtonOn] = useState<boolean>(showBackButton);

    if (items.length < 1 || isChanelContext) {
        return <Spacer />;
    }

    useEffect(() => {
        if (window.history.length <= 1) {
            setShowBackButtonOn(false);
        }
    }, []);

    return (
        <Background>
            <Wrapper>
                {showBackButtonOn && <BackButton model={fallbackNavigateBackItem} />}
                <BreadcrumbsContainer>
                    <Items>
                        {items.map((item, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <React.Fragment key={i}>
                                <Divider>/</Divider>
                                <BreadcrumbItem model={item} />
                            </React.Fragment>
                        ))}
                    </Items>
                </BreadcrumbsContainer>
            </Wrapper>
        </Background>
    );
}
